import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { TextField } from '@famly/mf_input_text';
import { StyledFormControl } from '@famly/mf_forms_form-control';
import { useDebounceControlledInputChange } from '@famly/stat_forms_debounce-controlled-input';
import { useAriaProps, useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { isE2e } from '@famly/mf_ts-utils_is-e2e';
import { hasValue } from '@famly/stat_ts-utils_has-value';
const isSetSelectionRangeSupported = (type) => {
    if (!type) {
        return false;
    }
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
    return ['password', 'search', 'tel', 'text', 'url'].includes(type);
};
export const FormTextField = ({ label, helperText, errorMessage, required, value, disabled, fullWidth, onClear, onChange, onBlur, onFocus, type = 'text', ...textFieldProps }) => {
    const ariaProps = useAriaProps(textFieldProps);
    const dataProps = useDataProps(textFieldProps);
    const inputRef = React.useRef(null);
    const { value: debouncedValue, handleChange: debouncedChange, handleBlur: propagateDebouncedValue, } = useDebounceControlledInputChange(value, onChange);
    React.useEffect(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        // Browsers (especially on mobile) autofill a field by writing the value straight to the
        // DOM without dispatching a React-visible `input` event, so `onChange` never fires and the
        // value never reaches the consuming form — leaving the field marked invalid even though it
        // looks filled. The only signal we get is MUI's `mui-auto-fill` keyframe animation, which
        // fires when the `:-webkit-autofill` pseudo-class matches. Read the autofilled value and
        // propagate it immediately (bypassing the debounce, which would otherwise delay it).
        const handleAutoFill = (event) => {
            if (event.animationName === 'mui-auto-fill') {
                onChange === null || onChange === void 0 ? void 0 : onChange(input.value);
            }
        };
        input.addEventListener('animationstart', handleAutoFill);
        return () => input.removeEventListener('animationstart', handleAutoFill);
    }, [onChange]);
    const textFieldId = React.useId();
    const labelId = React.useId();
    const errorMessageId = React.useId();
    const helperTextId = React.useId();
    const labelProps = React.useMemo(() => ({ id: labelId, htmlFor: textFieldId }), [labelId, textFieldId]);
    const errorMessageProps = React.useMemo(() => ({ id: errorMessageId }), [errorMessageId]);
    const helperTextProps = React.useMemo(() => ({ id: helperTextId }), [helperTextId]);
    const onChangeHandler = React.useCallback(e => {
        if (isE2e()) {
            onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value);
        }
        else {
            debouncedChange(e.target.value);
        }
    }, [debouncedChange, onChange]);
    const onKeyDownHandler = React.useCallback(e => {
        if (e.key === 'Enter') {
            propagateDebouncedValue();
        }
    }, [propagateDebouncedValue]);
    const onBlurHandler = React.useCallback(e => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        if (!isE2e()) {
            propagateDebouncedValue();
        }
    }, [onBlur, propagateDebouncedValue]);
    const onFocusHandler = React.useCallback(e => {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        if (isSetSelectionRangeSupported(type)) {
            // This makes sure that the focus cursor is at the end of the input's value
            e.target.setSelectionRange(e.target.value.length, e.currentTarget.value.length);
        }
    }, [onFocus, type]);
    return (_jsx(StyledFormControl, { fullWidth: fullWidth, disabled: disabled, label: label, labelProps: labelProps, helperText: helperText, helperTextProps: helperTextProps, required: required, errorMessage: errorMessage, errorMessageProps: errorMessageProps, onClear: onClear, children: _jsx(TextField, { ...textFieldProps, ...ariaProps, ...dataProps, ref: inputRef, type: type, id: textFieldId, value: isE2e() ? value : debouncedValue, error: hasValue(errorMessage), disabled: disabled, onKeyDown: onKeyDownHandler, onChange: onChangeHandler, onBlur: onBlurHandler, onFocus: onFocusHandler, ...(label ? { 'aria-labelledby': labelProps.id } : undefined), "aria-describedby": [ariaProps['aria-describedby'], helperText ? helperTextProps.id : undefined]
                .filter(Boolean)
                .join(' ') || undefined, "aria-errormessage": errorMessage ? errorMessageProps.id : undefined }) }));
};
